import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Chip, Paper, Grid, Button, Divider, Alert, CircularProgress,
} from '@mui/material';
import { VerifiedUser, DirectionsCar, AccountBalance, Upload, AccountBalanceWallet } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { getMyPartnerProfile } from '../../services/partnerUserService';
import { formatEmail, formatPhone } from '../../utils/contactMask';
import { formatMoney } from '../../utils/currency';
import { selectUser } from '../../store/slices/authSlice';
import { isPartnerPortalUserType } from '../../config/roleNavigation';

export default function PartnerSelfProfilePage() {
  const authUser = useSelector(selectUser);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  if (!isPartnerPortalUserType(authUser?.userType)) {
    return <Navigate to="/account" replace />;
  }
  const tab = params.get('tab') || 'profile';

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['partner-me'],
    queryFn: getMyPartnerProfile,
    refetchOnWindowFocus: true,
  });

  const user = data?.user;
  const walletBalance = data?.wallet?.balance ?? user?.walletBalance ?? 0;
  const walletFrozen = Boolean(data?.wallet?.isFrozen);
  const uploads = (data?.documents?.uploads || []) as Array<{
    _id: string;
    status: string;
    documentConfigId?: { name?: string; code?: string };
  }>;

  const onboardingFormCode = useMemo(() => {
    const entity = String(data?.entityType || user?.partnerEntityType || user?.userType || '').toLowerCase();
    const map: Record<string, string> = {
      driver: 'driver_onboarding',
      delivery_partner: 'delivery_partner_onboarding',
      merchant: 'merchant_onboarding',
      service_provider: 'service_provider_onboarding',
      vendor: 'event_organizer_onboarding',
      event_organizer: 'event_organizer_onboarding',
    };
    return map[entity] || 'driver_onboarding';
  }, [data?.entityType, user?.partnerEntityType, user?.userType]);

  const docSummary = useMemo(() => {
    const approved = uploads.filter((u) => u.status === 'approved').length;
    const pending = uploads.filter((u) => u.status === 'pending' || u.status === 'submitted').length;
    return { approved, pending, total: uploads.length };
  }, [uploads]);

  if (isLoading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      </PageShell>
    );
  }

  if (error || !user) {
    return (
      <PageShell>
        <Alert severity="error">Unable to load your profile. Please try again.</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="My Profile"
        subtitle="Documents, vehicle, and bank details"
        actions={
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => navigate(`/onboarding/${onboardingFormCode}`)}
          >
            Update Documents
          </Button>
        }
      />

      <Stack direction="row" spacing={1} mb={2}>
        {(['profile', 'vehicle', 'bank'] as const).map((key) => (
          <Chip
            key={key}
            label={key === 'profile' ? 'Profile & Documents' : key === 'vehicle' ? 'Vehicle' : 'Bank'}
            color={tab === key ? 'primary' : 'default'}
            onClick={() => navigate(`/my-profile?tab=${key}`)}
            variant={tab === key ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      {tab === 'profile' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Account</Typography>
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Name:</strong> {user.firstName} {user.lastName || ''}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {formatEmail(user.email, false)}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {formatPhone(user.phone, false)}</Typography>
                <Typography variant="body2"><strong>Status:</strong>{' '}
                  <Chip size="small" label={user.partnerStatus || 'active'} color={user.isVerified ? 'success' : 'warning'} />
                </Typography>
                {user.zoneId && (
                  <Typography variant="body2"><strong>Zone:</strong> {(user.zoneId as { name?: string }).name || '-'}</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AccountBalanceWallet color="primary" />
                  <Typography variant="h6" fontWeight={700}>Wallet</Typography>
                </Stack>
                <Button size="small" variant="outlined" onClick={() => refetch()} disabled={isFetching}>
                  {isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
              </Stack>
              <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mb: 0.5 }}>
                {formatMoney(walletBalance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Live balance from your wallet account{data?.wallet?.currency ? ` (${data.wallet.currency})` : ''}.
              </Typography>
              {walletFrozen && (
                <Chip size="small" color="warning" label="Wallet frozen" sx={{ mt: 1.5 }} />
              )}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Documents</Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {docSummary.approved} approved · {docSummary.pending} pending · {docSummary.total} total
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {uploads.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No documents uploaded yet.</Typography>
                )}
                {uploads.map((doc) => (
                  <Box key={doc._id} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{doc.documentConfigId?.name || 'Document'}</Typography>
                    <Chip size="small" label={doc.status} color={doc.status === 'approved' ? 'success' : 'warning'} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 'vehicle' && (
        <Paper sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <DirectionsCar color="primary" />
            <Typography variant="h6" fontWeight={700}>Vehicle Details</Typography>
          </Stack>
          {data?.driverProfile ? (
            <Grid container spacing={2}>
              {Object.entries(data.driverProfile).filter(([k]) => !['_id', '__v', 'tenantId', 'userId'].includes(k)).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Typography variant="caption" color="text.secondary">{key.replace(/([A-Z])/g, ' $1')}</Typography>
                  <Typography variant="body2" fontWeight={600}>{String(value ?? '-')}</Typography>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No vehicle profile yet. Complete driver onboarding to register your vehicle.
            </Typography>
          )}
        </Paper>
      )}

      {tab === 'bank' && (
        <Paper sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <AccountBalance color="primary" />
            <Typography variant="h6" fontWeight={700}>Bank Account</Typography>
          </Stack>
          {data?.bankDetails ? (
            <Grid container spacing={2}>
              {Object.entries(data.bankDetails).map(([key, value]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Typography variant="caption" color="text.secondary">{key}</Typography>
                  <Typography variant="body2" fontWeight={600}>{String(value ?? '-')}</Typography>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Bank details not on file. Add them during onboarding or contact support.
            </Typography>
          )}
        </Paper>
      )}
    </PageShell>
  );
}
